import { useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { Router, Route, useRouter } from './components/Router';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { HomePage } from './pages/HomePage';
import { ListingsPage } from './pages/ListingsPage';
import { ListingDetailsPage } from './pages/ListingDetailsPage';
import { ListingRedirect } from './components/ListingRedirect';
import { ActualitePage } from './pages/ActualitePage';
import { ArticleDetailPage } from './pages/ArticleDetailPage';
import AIEstimator from './pages/AIEstimator';
import { SubmitListingPage } from './pages/SubmitListingPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { MyListingsPage } from './pages/MyListingsPage';
import { EditListingPage } from './pages/EditListingPage';
import { AccountSettingsPage } from './pages/AccountSettingsPage';
import { CarPricesPage } from './pages/CarPricesPage';
import { CarBrandModelsPage } from './pages/CarBrandModelsPage';
import { CarModelDetailsPage } from './pages/CarModelDetailsPage';
import { CarVersionDetailsPage } from './pages/CarVersionDetailsPage';
import { ContactPage } from './pages/ContactPage';
import { AdminLogin } from './pages/admin/AdminLogin';
import { PendingRequests } from './pages/admin/PendingRequests';
import { ManageListings } from './pages/admin/ManageListings';
import { SettingsPage } from './pages/admin/SettingsPage';
import { Analytics } from './pages/admin/Analytics';
import { ActualiteManagement } from './pages/admin/ActualiteManagement';
import { BannerManagement } from './pages/admin/BannerManagement';
import { CarBrandsManagement } from './pages/admin/CarBrandsManagement';
import { CarModelsManagement } from './pages/admin/CarModelsManagement';
import { FeaturedModelsManagement } from './pages/admin/FeaturedModelsManagement';
import { HeroManagement } from './pages/admin/HeroManagement';
import { QuoteRequests } from './pages/admin/QuoteRequests'; // <--- ADDED THIS IMPORT
import { Analytics as VercelAnalytics } from '@vercel/analytics/react';

function AdminShortcut() {
  const { navigate } = useRouter();
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        navigate('/admin/login');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);
  return null;
}

function ScrollToTop() {
  const { currentPath } = useRouter();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPath]);
  return null;
}

function GlobalFooter() {
  const { currentPath } = useRouter();
  if (currentPath.startsWith('/admin')) {
    return null;
  }
  return <Footer />;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <VercelAnalytics />
          <AdminShortcut />
          
          {/* Public Routes */}
          <Route path="/">
            <HomePage />
          </Route>
          <Route path="/annonces">
            <Header />
            <ListingsPage />
          </Route>
          <Route path="/annonces/:slug">
            <Header />
            <ListingDetailsPage />
          </Route>
          <Route path="/listing/:id">
            <ListingRedirect />
          </Route>
          <Route path="/actualite">
            <Header />
            <ActualitePage />
          </Route>
          <Route path="/actualite/:slug">
            <Header />
            <ArticleDetailPage />
          </Route>
          <Route path="/estimateur">
            <Header />
            <AIEstimator />
          </Route>
          <Route path="/pulblication">
            <Header />
            <SubmitListingPage />
          </Route>
          <Route path="/soumettre-annonce">
            <Header />
            <SubmitListingPage />
          </Route>
          
          {/* Auth Routes */}
          <Route path="/connecter">
            <LoginPage />
          </Route>
          <Route path="/registre">
            <RegisterPage />
          </Route>
          <Route path="/mot-de-passe-oublie">
            <ForgotPasswordPage />
          </Route>
          <Route path="/reinitialiser-motdepasse">
            <ResetPasswordPage />
          </Route>
          <Route path="/vérifier-email">
            <VerifyEmailPage />
          </Route>
          <Route path="/auth/callback">
            <AuthCallbackPage />
          </Route>

          {/* User Dashboard Routes */}
          <Route path="/mes-annonces">
            <Header />
            <ProtectedRoute>
              <MyListingsPage />
            </ProtectedRoute>
          </Route>
          <Route path="/modifier-annonce/:id">
            <Header />
            <ProtectedRoute>
              <EditListingPage />
            </ProtectedRoute>
          </Route>
          <Route path="/paramètres-du-compte">
            <Header />
            <ProtectedRoute>
              <AccountSettingsPage />
            </ProtectedRoute>
          </Route>
          
          {/* Car Prices Routes */}
          <Route path="/neuf">
            <Header />
            <CarPricesPage />
          </Route>
          <Route path="/neuf/:brandSlug">
            <Header />
            <CarBrandModelsPage />
          </Route>
          <Route path="/neuf/:brandSlug/:modelSlug">
            <Header />
            <CarModelDetailsPage />
          </Route>
          <Route path="/neuf/:brandSlug/:modelSlug/:versionSlug">
            <Header />
            <CarVersionDetailsPage />
          </Route>
          <Route path="/car-prices">
            <Header />
            <CarPricesPage />
          </Route>
          <Route path="/car-prices/:brandId">
            <Header />
            <CarBrandModelsPage />
          </Route>
          <Route path="/car-prices/:brandId/:modelId">
            <Header />
            <CarModelDetailsPage />
          </Route>
          <Route path="/car-prices/:brandId/:modelId/:versionId">
            <Header />
            <CarVersionDetailsPage />
          </Route>
          
          {/* Contact Route */}
          <Route path="/contact">
            <Header />
            <ContactPage />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login">
            <AdminLogin />
          </Route>
          <Route path="/admin/dashboard">
            <ProtectedRoute requireAdmin={true}>
              <PendingRequests />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/listings">
            <ProtectedRoute requireAdmin={true}>
              <ManageListings />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/banners">
            <ProtectedRoute requireAdmin={true}>
              <BannerManagement />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/hero">
            <ProtectedRoute requireAdmin={true}>
              <HeroManagement />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/actualite">
            <ProtectedRoute requireAdmin={true}>
              <ActualiteManagement />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/car-brands">
            <ProtectedRoute requireAdmin={true}>
              <CarBrandsManagement />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/car-models">
            <ProtectedRoute requireAdmin={true}>
              <CarModelsManagement />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/featured-models">
            <ProtectedRoute requireAdmin={true}>
              <FeaturedModelsManagement />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/analytics">
            <ProtectedRoute requireAdmin={true}>
              <Analytics />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/settings">
            <ProtectedRoute requireAdmin={true}>
              <SettingsPage />
            </ProtectedRoute>
          </Route>
          <Route path="/admin/quote-requests">
            <ProtectedRoute requireAdmin={true}>
              <QuoteRequests />
            </ProtectedRoute>
          </Route>

          <GlobalFooter />
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;